'use client'

import { RawStoreData } from '@/lib/stores/store-types'
import { Button } from '../ui/button'
import { StoreMarkerIcon } from '../StoreMarkerIcon'
import StoreDetailHours from '../StoreDetailHours'
import Link from 'next/link'
import StoreDetailMap from '../StoreDetailMap'
import { formatPhoneNumber } from '@/lib/i18n/format-phone-number'
import { hasHours } from '@/lib/store-hours'
import { useEffect, useState } from 'react'
import { getCurrentTimeAtLocation } from '@/lib/client-get-time-at-lat-lng'
import NavigationBar from '../NavigationBar'
import { prepareHtmlForAboutSection, getFullStreetAddress } from '@/lib/utils'
import { Locale } from '@/lib/i18n'

export default function StoreDetailPage({
	store,
	dictionary,
	locale,
}: {
	store: RawStoreData
	dictionary: Record<string, string>
	locale: string
}) {
	const [currentTime, setCurrentTime] = useState<Date>(new Date())

	useEffect(() => {
		getCurrentTimeAtLocation(store.coordinates).then((date) => {
			setCurrentTime(date)
		})
	}, [store.coordinates])
	const showHours = hasHours(store)
	return (
		<div className="flex flex-col lg:flex-row w-full h-screen bg-white" role="main">
			{/* Left Panel */}
			<div className="w-full lg:w-[400px] flex flex-col border-r border-gray-200" role="complementary">
				<div className="flex flex-row gap-2 justify-between items-center border-b border-gray-200">
					<NavigationBar
						dictionary={dictionary}
						locale={locale as Locale}
						visibleItems={['back-button', 'store-index', 'return-to-locator', 'return-policy']}
					/>
				</div>

				{/* Store Details */}
				<div className="p-4 border-b border-gray-200">
					<div className="flex items-start space-x-3">
						<StoreMarkerIcon storeType={store.store_type} size={24} aria-hidden="true" />
						<div>
							<h1 className="text-xl font-bold">{store.name}</h1>
							<p className="text-sm mt-4">{getFullStreetAddress(store.address)}</p>
							<p className="text-sm">
								{store.address.city}, {store.address.state} {store.address.zip}
							</p>
							<a
								href={`tel:${store.phone}`}
								className="text-sm text-blue-600 block mt-2"
								aria-label={`${dictionary['call-store']} ${formatPhoneNumber(store.address.country, store.phone)}`}
							>
								{formatPhoneNumber(store.address.country, store.phone)}
							</a>
							<div className="mt-4 flex flex-row gap-2 items-center justify-between">
								<Button
									size="sm"
									className="bg-black hover:bg-gray-800 text-white text-xs px-3 rounded"
									aria-label={dictionary['get-directions']}
								>
									<Link
										href={`https://www.google.com/maps/dir/?api=1&destination=${store.coordinates.latitude},${store.coordinates.longitude}`}
										target="_blank"
									>
										{dictionary['get-directions']}
									</Link>
								</Button>

								{/* Social Links */}
								{(store.social_media.facebook || store.social_media.instagram || store.social_media.twitter) && (
									<div className="p-4 flex-1">
										<div className="flex space-x-4" role="group" aria-label={dictionary['social-media-links']}>
											{store.social_media.facebook && (
												<a
													href={store.social_media.facebook}
													target="_blank"
													rel="noopener noreferrer"
													className="text-black"
													data-testid="facebook-link"
													aria-label={`${dictionary['facebook']} - ${dictionary['opens-in-new-tab']}`}
												>
													<svg
														xmlns="http://www.w3.org/2000/svg"
														width="24"
														height="24"
														viewBox="0 0 24 24"
														fill="currentColor"
														aria-hidden="true"
													>
														<path d="M9 8h-3v4h3v12h5v-12h3.642l.358-4h-4v-1.667c0-.955.192-1.333 1.115-1.333h2.885v-5h-3.808c-3.596 0-5.192 1.583-5.192 4.615v3.385z" />
													</svg>
												</a>
											)}
											{store.social_media.instagram && (
												<a
													href={store.social_media.instagram}
													target="_blank"
													rel="noopener noreferrer"
													className="text-black"
													data-testid="instagram-link"
													aria-label={`${dictionary['instagram']} - ${dictionary['opens-in-new-tab']}`}
												>
													<svg
														xmlns="http://www.w3.org/2000/svg"
														width="24"
														height="24"
														viewBox="0 0 24 24"
														fill="currentColor"
														aria-hidden="true"
													>
														<path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
													</svg>
												</a>
											)}
											{store.social_media.twitter && (
												<a
													href={store.social_media.twitter}
													target="_blank"
													rel="noopener noreferrer"
													className="text-black"
													data-testid="twitter-link"
													aria-label={`${dictionary['twitter']} - ${dictionary['opens-in-new-tab']}`}
												>
													<svg
														xmlns="http://www.w3.org/2000/svg"
														width="24"
														height="24"
														viewBox="0 0 24 24"
														fill="currentColor"
														aria-hidden="true"
													>
														<path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
													</svg>
												</a>
											)}
										</div>
									</div>
								)}
							</div>
						</div>
					</div>
				</div>

				{/* Store Hours */}
				{showHours && store.openInfo !== 'CLOSED_PERMANENTLY' && (
					<div className="p-4 border-b border-gray-200">
						<h2 className="font-bold mb-4">{dictionary['store-hours']}</h2>
						{store.openInfo === 'CLOSED_TEMPORARILY' && (
							<div className="text-orange-600 font-semibold mb-4">{dictionary['temporarily-closed']}</div>
						)}
						<StoreDetailHours locale={locale} store={store} dictionary={dictionary} currentTime={currentTime} />
					</div>
				)}
				{store.openInfo === 'CLOSED_PERMANENTLY' && (
					<div className="p-4 border-b border-gray-200">
						<div className="text-red-600 font-semibold">{dictionary['permanently-closed']}</div>
					</div>
				)}

				{store.aboutStore && (
					<div
						className="p-4 border-b border-gray-200 overflow-scroll"
						role="region"
						aria-label={dictionary['about-store']}
					>
						<h2 className="font-bold mb-4">{dictionary['about-store']}</h2>
						<div className="text-sm">
							{store.aboutStore.split('\n').map((section, index) => (
								<section
									className="mt-2 about-store-section"
									key={index}
									dangerouslySetInnerHTML={{ __html: prepareHtmlForAboutSection(section) }}
								/>
							))}
						</div>
					</div>
				)}
			</div>

			{/* Map */}
			<div className="flex-1 relative">
				<div className="absolute inset-0 bg-gray-200">
					<StoreDetailMap locale={locale} store={store} dictionary={dictionary} currentTime={currentTime} />
				</div>
			</div>
		</div>
	)
}
